"use client";
import React from "react";

import {
  Box,
  BoxSx,
  Grid,
  GridColumn,
  GridSizeArray,
  Target
} from "../../foundations";
import { Icon } from "../../foundations/Icon/Icon";
import { Text } from "../../foundations/Typography/Text";
import { useWarning } from "../../hooks/useWarning";
import { Button } from "../Button";
import { Overlay } from "../Overlay";

type ModalSize = "xs" | "sm" | "md" | "lg";
interface ModalDimensions {
  modalSize: GridSizeArray;
  modalPush: GridSizeArray;
}

export type ModalButtonProps = React.ComponentProps<typeof Target> & {
  label: string;
};

export type ModalProps = {
  title?: string;
  isOpen: boolean;
  onDismiss?: () => void;
  size?: ModalSize;
  buttons?: ModalButtonProps[];
} & React.ComponentProps<typeof Box>;

const useFocusTrap = (
  isActive: boolean,
  modalRef: React.RefObject<HTMLElement>
) => {
  React.useEffect(() => {
    if (!isActive || !modalRef.current) {
      return;
    }

    const modalSpace = modalRef.current;

    const focusableSelectors =
      'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(
      modalSpace.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };
    modalSpace.addEventListener("keydown", handleKeyDown);
    firstElement?.focus(); // first focusable element is focused on mount
    return () => {
      modalSpace?.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, modalRef]);
};

export const Modal: React.FC<ModalProps> = ({
  children,
  title,
  isOpen,
  onDismiss,
  size = "md",
  buttons
}) => {
  const { warn } = useWarning();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const clickingContent = React.useRef(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [showDivider, setShowDivider] = React.useState(false);
  const dismissible = !!onDismiss;
  const transform: BoxSx["transform"] = isOpen
    ? ["translateY(0px)", "unset", null]
    : ["translateY(100%)", "unset", null];

  const { modalSize, modalPush } = React.useMemo((): ModalDimensions => {
    const modalDimensions: ModalDimensions = {
      modalSize: [4, 6, 8],
      modalPush: [0, 1, 2]
    };
    switch (size) {
      case "xs":
        modalDimensions.modalSize = [4, 4, 4];
        modalDimensions.modalPush = [0, 2, 4];
        break;
      case "sm":
        modalDimensions.modalSize = [4, 4, 6];
        modalDimensions.modalPush = [0, 2, 3];
        break;
      case "lg":
        modalDimensions.modalSize = [4, 8, 10];
        modalDimensions.modalPush = [0, 0, 1];
        break;
    }
    return modalDimensions;
  }, [size]);

  const handleClickOutside = React.useCallback(() => {
    if (!clickingContent.current) {
      if (onDismiss && dismissible) {
        onDismiss();
      }
    }
    clickingContent.current = false;
  }, [dismissible, onDismiss]);

  const dismissControl = React.useMemo<BoxSx>(
    () => ({
      color: "control-enabled",
      ":hover": { color: "control-hover" },
      ":active": { color: "control-click" },
      ":focus-visible": {
        outline: "2px solid",
        outlineColor: "focus",
        outlineOffset: "2px",
        borderRadius: "radius-round"
      }
    }),
    []
  );

  useFocusTrap(isOpen, modalRef);

  if (buttons && buttons.length > 2) {
    warn(
      "Modal: Adding more than two buttons may impact layout on smaller screens."
    );
  }

  // calculate children height and show divider if scrollable content exists
  React.useEffect(() => {
    const updateDividerVisibility = () => {
      const content = contentRef.current;
      if (content) {
        const { scrollTop, scrollHeight, clientHeight } = content;
        setShowDivider(
          scrollHeight > clientHeight &&
            (scrollTop > 0 || scrollTop + clientHeight < scrollHeight)
        );
      }
    };
    updateDividerVisibility(); // initialize visibility on mount or modal open
    const content = contentRef.current;
    if (content) {
      content.addEventListener("scroll", updateDividerVisibility);
      return () =>
        content.removeEventListener("scroll", updateDividerVisibility);
    }
  }, [isOpen]); // recalculate on modal open

  return (
    <Overlay
      visible={isOpen}
      onDismiss={onDismiss}
      sx={{
        alignItems: "center",
        display: "flex"
      }}
    >
      <Grid initial={true} onClick={handleClickOutside}>
        <GridColumn
          size={modalSize}
          fillGap={true}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: ["flex-end", "center", null],
            minHeight: ["100dvh", "100vh"],
            transition: "transition-ease-out-medium",
            transform: transform
          }}
          push={modalPush}
        >
          <Box
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? "modal-title" : undefined}
            onClick={() => {
              clickingContent.current = true;
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              cursor: "initial",
              maxHeight: (theme) => `calc(100vh - ${theme.sizes[28]}px)`,
              overflow: "auto",
              borderRadius: [null, "radius-2"],
              border: "1px solid",
              borderColor: "stroke-neutral-default",
              boxShadow: [null, "shadow-100"],
              py: "spacing-7",
              gap: "spacing-5"
            }}
          >
            {(title || dismissible) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: title ? "space-between" : "flex-end",
                  alignItems: "center",
                  flexShrink: 0,
                  px: "spacing-7"
                }}
              >
                {title && (
                  <Text variant="title-1-medium" id="modal-title">
                    {title}
                  </Text>
                )}
                {dismissible && (
                  <Target
                    onClick={() => {
                      clickingContent.current = true;
                      onDismiss();
                    }}
                    aria-label="Close Modal"
                    sx={{
                      ...dismissControl,
                      height: "spacing-7",
                      width: "spacing-7"
                    }}
                  >
                    <Icon icon="x" size="sm" color="currentColor" />
                  </Target>
                )}
              </Box>
            )}
            <Box
              ref={contentRef}
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                px: "spacing-7",
                // using border + padding to visually differentiate the scroll area
                py: showDivider ? "spacing-5" : "spacing-none",
                borderBottom: showDivider ? "1px solid" : "none",
                borderTop: showDivider ? "1px solid" : "none",
                borderColor: "stroke-neutral-default"
              }}
            >
              {children}
            </Box>
            {buttons && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  alignSelf: "stretch",
                  gap: "spacing-3",
                  px: "spacing-7"
                }}
              >
                {buttons.map((button, idx) => {
                  return (
                    <Button
                      key={`modal-button-${idx}`}
                      variant={idx < buttons.length - 1 ? "outline" : "filled"}
                      {...button}
                    >
                      {button.label}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>
        </GridColumn>
      </Grid>
    </Overlay>
  );
};
